import { useMemo, useState } from 'react';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import Drawer from '@mui/material/Drawer';
import Tooltip from '@mui/material/Tooltip';
import { styled } from '@mui/material/styles';
import { HrefLink } from '@bloomays-lib/ui.shared';
import { DataGridV2, StatusChip, TONES } from '@bloomays-lib/ui.shared/datagrid';
import type { DataGridV2Column, DataGridV2Tone } from '@bloomays-lib/ui.shared/datagrid';
import { useTranslation } from 'react-i18next';
import { getJobTypeGroupLabel, getPrivateAdminDashboardMissionPath } from '@bloomays-lib/utils.shared';
import {
  CRAStatus,
  JobType,
  MISSION_PRIME_ERROR_CRA_STATUS,
  PaymentTerms,
  PRIME_LINE_ERROR_CODES,
} from '@bloomays-lib/types.shared';
import { TamPrimeLine } from '../../types/primes';
import {
  formatPrimeEuro,
  formatPrimeRate,
  isPrimeLineError,
  shouldShowMissionActivitiesLinkForCraStatus,
} from '../../utils/monthlyPrime.utils';
import TamPrimeDetailPanel from './TamPrimeDetailPanel';

/**
 * Ton de couleur du tag par statut de CRA, aligné sur la charte du dashboard CRA
 * (voir `CRA_CHIP` dans `helpers/craDashboard.ts`).
 */
const CRA_STATUS_TONE: Record<CRAStatus | typeof MISSION_PRIME_ERROR_CRA_STATUS, DataGridV2Tone> = {
  'Validé client': 'success',
  'Forcé Bloomays': 'success',
  'Attente signature Client': 'warning',
  'Attente signature Bloomer': 'warning',
  'Non créé': 'danger',
  'Signature non créee': 'danger',
  'Non travaillé': 'neutral',
  [MISSION_PRIME_ERROR_CRA_STATUS]: 'danger',
};

const getCraStatusTone = (craStatus: string): DataGridV2Tone =>
  CRA_STATUS_TONE[craStatus as CRAStatus | typeof MISSION_PRIME_ERROR_CRA_STATUS] ?? 'neutral';

const CRA_STATUS_OPTIONS: Array<{ value: string; label: string }> = (
  Object.keys(CRA_STATUS_TONE) as Array<CRAStatus | typeof MISSION_PRIME_ERROR_CRA_STATUS>
).map((value) => ({ value, label: value }));

export type MonthlyPrimeTableProps = {
  rows: TamPrimeLine[];
  loading: boolean;
  referenceDate: Date;
  showOnlyMargin: boolean;
};

const formatPaymentTermsLabel = (paymentTerms: PaymentTerms | undefined, t: (key: string) => string): string => {
  switch (paymentTerms) {
    case PaymentTerms.FIFTY_FIFTY_SIGNATURE_START:
      return t('paymentTermsFiftyFiftySignatureStart');
    case PaymentTerms.FIFTY_FIFTY_START_TRIAL:
      return t('paymentTermsFiftyFiftyStartTrial');
    case PaymentTerms.FULL_START:
      return t('paymentTermsFullStart');
    case PaymentTerms.CUSTOM:
      return t('paymentTermsCustom');
    default:
      return t('paymentTermsUndefined');
  }
};

const formatContractTypeLabel = (row: TamPrimeLine, t: (key: string) => string): string => {
  const contractType = getJobTypeGroupLabel(row.jobType as JobType);

  if (row.__typename === 'BillingPrime') {
    const paymentTermsLabel = formatPaymentTermsLabel(row.paymentTerms ?? undefined, t);
    return `${contractType} ${paymentTermsLabel}`;
  }

  return contractType;
};

const buildContractTypeOptions = (
  rows: TamPrimeLine[],
  t: (key: string) => string,
): Array<{ value: string; label: string }> =>
  [...new Set(rows.map((row) => formatContractTypeLabel(row, t)).filter(Boolean))]
    .sort((a, b) => a.localeCompare(b, 'fr'))
    .map((value) => ({ value, label: value }));

type ContractTypeCellProps = {
  row: TamPrimeLine;
  t: (key: string) => string;
};

const ContractTypeCell = ({ row, t }: ContractTypeCellProps) => {
  const contractType = getJobTypeGroupLabel(row.jobType as JobType);

  if (row.__typename !== 'BillingPrime') {
    return <ContractTypeLabel>{contractType}</ContractTypeLabel>;
  }

  const paymentTermsLabel = formatPaymentTermsLabel(row.paymentTerms ?? undefined, t);

  return (
    <ContractTypeCellContent>
      <ContractTypeLabel>{contractType}</ContractTypeLabel>
      <PaymentTermsLabel>{paymentTermsLabel}</PaymentTermsLabel>
    </ContractTypeCellContent>
  );
};

const MonthlyPrimeTable = ({ rows, loading, referenceDate, showOnlyMargin }: MonthlyPrimeTableProps) => {
  const { t } = useTranslation(['primes']);
  const [selectedLine, setSelectedLine] = useState<TamPrimeLine | undefined>();

  const columns: DataGridV2Column<TamPrimeLine>[] = useMemo(() => {
    const contractTypeOptions = buildContractTypeOptions(rows, t);

    return [
      {
        field: 'clientSocietyName',
        header: t('columnSociety'),
        flex: 1,
        minWidth: 120,
        searchText: (row) => row.clientSocietyName,
      },
      {
        field: 'bloomerFullName',
        header: t('columnBloomer'),
        flex: 1,
        minWidth: 140,
        searchText: (row) => row.bloomerFullName,
      },
      {
        field: 'jobType',
        header: t('columnContractType'),
        flex: 1.4,
        minWidth: 280,
        valueGetter: (row) => formatContractTypeLabel(row, t),
        valueOptions: contractTypeOptions,
        searchText: (row) => formatContractTypeLabel(row, t),
        render: (row) => <ContractTypeCell row={row} t={t} />,
      },
      {
        field: 'craStatus',
        header: t('columnCraStatus'),
        minWidth: 230,
        flex: 1,
        valueGetter: (row) => ('craStatus' in row ? row.craStatus : ''),
        valueOptions: CRA_STATUS_OPTIONS,
        searchText: (row) => ('craStatus' in row ? row.craStatus : ''),
        render: (row) => {
          const craStatus = 'craStatus' in row ? row.craStatus : '';
          if (!craStatus) {
            return null;
          }
          const missionActivitiesHref =
            shouldShowMissionActivitiesLinkForCraStatus(craStatus) && row.airtableId.trim() !== ''
              ? `${getPrivateAdminDashboardMissionPath(row.airtableId)}/activities`
              : undefined;
          return (
            <CraStatusCell>
              <CraStatusChipWrap>
                <StatusChip tone={getCraStatusTone(craStatus)}>{craStatus}</StatusChip>
              </CraStatusChipWrap>
              {missionActivitiesHref ? (
                <CraStatusLinkWrap>
                  <HrefLink href={missionActivitiesHref} target="_blank" title={t('openMissionActivities')}>
                    <OpenInNewIcon fontSize="small" />
                  </HrefLink>
                </CraStatusLinkWrap>
              ) : null}
            </CraStatusCell>
          );
        },
      },
      {
        field: 'monthlyMargin',
        header: t('columnMargin'),
        width: 110,
        align: 'right',
        variant: 'custom',
        valueGetter: (row) => row.monthlyMargin,
        searchText: (row) => formatPrimeEuro(row.monthlyMargin),
        render: (row) => {
          const marginLabel = formatPrimeEuro(row.monthlyMargin);
          if (row.errorCode !== PRIME_LINE_ERROR_CODES.MISSING_PRICING) {
            return marginLabel;
          }
          return (
            <AlertCell>
              {marginLabel}
              <Tooltip title={t('missingPricingTooltip')}>
                <span role="img" aria-label={t('missingPricingTooltip')}>
                  🚨
                </span>
              </Tooltip>
            </AlertCell>
          );
        },
      },
      {
        field: 'monthlyPrime',
        header: t('columnCommission'),
        width: 120,
        align: 'right',
        variant: 'custom',
        valueGetter: (row) => row.monthlyPrime,
        searchText: (row) => formatPrimeEuro(row.monthlyPrime),
        render: (row) => <MoneyText>{formatPrimeEuro(row.monthlyPrime)}</MoneyText>,
      },
      {
        field: 'splitRate',
        header: t('columnTamShare'),
        width: 110,
        align: 'right',
        valueGetter: (row) => formatPrimeRate(row.splitRate),
        searchText: (row) => formatPrimeRate(row.splitRate),
      },
      {
        field: 'commissionRate',
        header: t('columnCommissionRate'),
        width: 130,
        align: 'right',
        valueGetter: (row) => formatPrimeRate(row.commissionRate),
        searchText: (row) => formatPrimeRate(row.commissionRate),
      },
    ];
  }, [rows, t]);

  return (
    <TableSection>
      <DataGridV2
        uniqueId="monthly-prime-lines"
        rows={rows}
        columns={columns}
        loading={loading}
        getRowId={(row: TamPrimeLine) => `${row.jobFunnelProjectId}-${row.airtableId}`}
        getRowTone={(row) => (isPrimeLineError(row) ? 'danger' : undefined)}
        onRowClick={(row) => setSelectedLine(row)}
        sortingMode="client"
        filterMode="client"
        pageSize={25}
        pagination={rows.length > 25}
        hideFooter={rows.length <= 25}
        search
        searchPlaceholder={t('searchPlaceholder')}
        rowGrouping={false}
      />

      <Drawer anchor="right" open={!!selectedLine} onClose={() => setSelectedLine(undefined)}>
        {selectedLine ? (
          <TamPrimeDetailPanel
            line={selectedLine}
            referenceDate={referenceDate}
            onClose={() => setSelectedLine(undefined)}
            showOnlyMargin={showOnlyMargin}
          />
        ) : null}
      </Drawer>
    </TableSection>
  );
};

export default MonthlyPrimeTable;

const TableSection = styled('div')(() => ({
  marginTop: '24px',
  width: '100%',
}));

const AlertCell = styled('div')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(0.75),
}));

/** Reproduit le style de `MoneyCell` (ui.shared) pour un montant à 2 décimales fixes. */
const MoneyText = styled('span')(() => ({
  fontSize: 13,
  fontWeight: 700,
  whiteSpace: 'nowrap',
  color: TONES.success.color,
}));

const CraStatusCell = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.75),
  width: '100%',
  minWidth: 0,
}));

const CraStatusChipWrap = styled('div')(() => ({
  minWidth: 0,
  overflow: 'hidden',
}));

const CraStatusLinkWrap = styled('span')(() => ({
  display: 'inline-flex',
  alignItems: 'center',
  flexShrink: 0,
}));

const ContractTypeCellContent = styled('div')(({ theme }) => ({
  display: 'inline-flex',
  flexWrap: 'nowrap',
  alignItems: 'center',
  gap: theme.spacing(1),
  width: '100%',
  minWidth: 0,
}));

const ContractTypeLabel = styled('span')(() => ({
  whiteSpace: 'nowrap',
  flexShrink: 0,
}));

const PaymentTermsLabel = styled('span')(({ theme }) => ({
  fontSize: '11px',
  lineHeight: '14px',
  color: theme.palette.text.secondary,
  whiteSpace: 'nowrap',
}));
